import { createSelector } from 'reselect';
import {AssetGroup, AssetGroupAvailability, Location, LocationBookingStats} from '../api/types';
import Permission from '../common/permissions';
import { ReduxState } from '../reducer/reducer';
import { makePermissionListSelector } from './authenticationSelector';
import { RootState } from '../store/store';
import { STATION_TIME_ZONE, zoneOfLocation } from '../utils/stationTime';

const stateKey = 'locations';

export const locationListSelector = (state: RootState): Location[] => state.legacy[stateKey].locations;
export const bookingStatsSelector = (state: RootState): Record<number, LocationBookingStats> =>
    state.legacy[stateKey].bookingStats;

export const makeLocationSelector = (locationId: string) => {
    return createSelector(
        [locationListSelector],
        (locationsList): Location|undefined => {
            return locationsList.find(location => location.id === locationId);
        },
    );
};

/**
 * #3219: the zone of location `locationId` — the ONE lookup from a location id
 * to its clock for screens that hold an id but no zone (create forms, the
 * visitor booking page). Reads the admin location list (`GET location`,
 * loaded at sign-in). A location not (yet) in it, or one without a stored
 * zone, reads as `fallbackZone` — an edit form passes the booking's own
 * `locationTimeZone` — and else as the station default. Screens that only
 * read a booking's details use the payload's `locationTimeZone` directly.
 * `locationTimeZoneIn` is the same lookup for a list already in hand (a view
 * that resolves many locations at once).
 */
export const locationTimeZoneIn = (
    locations: readonly Location[],
    locationId: string | null | undefined,
    fallbackZone?: string | null,
): string =>
    zoneOfLocation(
        (locationId ? locations.find((location) => location.id === locationId)?.timeZone : undefined)
            || fallbackZone,
    );

export const selectLocationTimeZone = (
    state: RootState,
    locationId: string | null | undefined,
    fallbackZone?: string | null,
): string => locationTimeZoneIn(locationListSelector(state), locationId, fallbackZone);

/**
 * #3219: the zone a set of locations shares — the station default when they
 * disagree or there are none. A search that runs before any one location is
 * picked (the apartment search by city) is one place, so its candidate
 * locations share a clock; were they ever to disagree there is no single window
 * to search with, and the default is what the server assumes for a request
 * without a location. Mirrors apps/end-user/src/util/locationTimeZone.ts
 * `sharedZoneOf`.
 */
export const sharedZoneOf = (locations: readonly Pick<Location, 'timeZone'>[]): string => {
    const zones = new Set(locations.map((location) => zoneOfLocation(location.timeZone)));
    return zones.size === 1 ? Array.from(zones)[0] : STATION_TIME_ZONE;
};

/**
 * #3219: the zone of the locations in `city` — the candidates the server
 * visits for an apartment/venue search by city (`apartments/city/{city}/…`,
 * `LocationPGService.getLocationIdsByCity`). The station default without a
 * city.
 */
export const cityTimeZoneIn = (locations: readonly Location[], city: string | null | undefined): string =>
    city ? sharedZoneOf(locations.filter((location) => location.address?.city === city)) : STATION_TIME_ZONE;

export const selectCityTimeZone = (state: RootState, city: string | null | undefined): string =>
    cityTimeZoneIn(locationListSelector(state), city);

// TODO: Implement show permission for locations
export const makePermittedLocationListSelector = () => {
    return createSelector(
        [locationListSelector, makePermissionListSelector()],
        (locationsList, permissionsList): Location[] => {
            return locationsList.filter(
                location => permissionsList.some(
                    /* Either full permission to access the given station or for at least one door of this station */
                    permission => {
                        const hasFullAccess = permission === Permission.editLocations();
                        const hasAccessToLocation = permission === Permission.editLocation(location.id);
                        return hasFullAccess || hasAccessToLocation;
                    },
                ),
            );
        },
    );
};

export const makeStatsPermittedLocationListSelector = () => {
    return createSelector(
        [locationListSelector, makePermissionListSelector()],
        (locationsList, permissionsList): Location[] => {
            if (permissionsList.includes(Permission.accessLocationsBookingStats())) {
                return locationsList;
            }

            return locationsList.filter(
                location => permissionsList.includes(Permission.accessLocationBookingStats(location.id)),
            );
        },
    );
};

export const makeLocationBookingStatsSelector = (locationId: number) => {
    return createSelector(
        [bookingStatsSelector],
        (bookingStats): LocationBookingStats|undefined => {
            return bookingStats[locationId];
        },
    );
};

export const makeLocationAssetGroupAvailabilities = (locationId: string, groupId: string) =>
    (state: ReduxState): AssetGroupAvailability | null => {
        const assetGroups: AssetGroup[] = state[stateKey].locations.find(
            location => location.id === locationId)?.assetGroups ?? [];
        // const assetGroups: AssetGroups = state[stateKey].locations[locationId];
        // TODO check why the location id is used as key
        const group = assetGroups.find(group => group.id === groupId);
        return group?.availabilities ?? null;
    };

export const makeLocationAssetGroups = (locationId: string) => (state: ReduxState): AssetGroup[] | null => {
    for (const location of state[stateKey].locations) {
        if (location.id === locationId) {
            return location.assetGroups || null;
        }
    }
    return null;
};
